import { CheckCheck, Send, Trash2, Wallet } from 'lucide-react'
import type { BulkTransitionAction, InvoiceListItem, InvoiceStatus } from '@/lib/invoice-types'

export interface BulkAction {
  action: BulkTransitionAction
  label: string
  icon: typeof Send
  needsIssueDate?: boolean
  danger?: boolean
}

export interface BulkGroup {
  status: InvoiceStatus
  ids: string[]
  actions: BulkAction[]
}

/**
 * Bulk-acties hangen aan de status van de geselecteerde regels, niet aan het
 * actieve filter. Dat is precies wat het mogelijk maakt om de statussen te
 * groeperen in segmenten: anders zou elke status een eigen tab moeten houden.
 *
 * De volgorde is de levensloop en bepaalt ook de volgorde in de bulkbalk.
 */
export const BULK_ACTIONS: Partial<Record<InvoiceStatus, BulkAction[]>> = {
  DRAFT: [
    { action: 'delete', label: 'Verwijderen', icon: Trash2, danger: true },
    { action: 'submit', label: 'Submitten', icon: Send },
    { action: 'direct_approve', label: 'Direct approven', icon: CheckCheck, needsIssueDate: true },
  ],
  PARKED: [{ action: 'delete', label: 'Verwijderen', icon: Trash2, danger: true }],
  PENDING_APPROVAL: [{ action: 'approve', label: 'Approven', icon: CheckCheck, needsIssueDate: true }],
  APPROVED: [{ action: 'send', label: 'Versturen', icon: Send }],
  SENT: [{ action: 'mark_paid', label: 'Betaald markeren', icon: Wallet }],
  ESCALATED: [{ action: 'mark_paid', label: 'Betaald markeren', icon: Wallet }],
}

export function isSelectable(invoice: InvoiceListItem): boolean {
  return BULK_ACTIONS[invoice.status] !== undefined
}

/**
 * De selectie mag door de statussen heen lopen, maar een transitie niet: submitten
 * betekent iets anders dan versturen. De selectie valt daarom uiteen in groepen per
 * status, elk met de acties die bij die status horen, en elke knop raakt alleen zijn
 * eigen groep.
 */
export function bulkGroups(invoices: InvoiceListItem[], selected: ReadonlySet<string>): BulkGroup[] {
  return (Object.entries(BULK_ACTIONS) as [InvoiceStatus, BulkAction[]][])
    .map(([status, actions]) => ({
      status,
      actions,
      ids: invoices.filter((invoice) => invoice.status === status && selected.has(invoice.id)).map((invoice) => invoice.id),
    }))
    .filter((group) => group.ids.length > 0)
}
